import React, { useState, useEffect } from "react";
import config from "../../config/config";

const SEOManager = () => {
  const [seoSettings, setSeoSettings] = useState({
    page_title: "",
    meta_description: "",
    meta_keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    canonical_url: "",
    robots: "index,follow",
    structured_data: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(config.getAdminApiUrl("get_seo_settings.php"));
      const data = await response.json();

      if (data.success) {
        setSeoSettings(
          data.settings || {
            page_title: "",
            meta_description: "",
            meta_keywords: "",
            og_title: "",
            og_description: "",
            og_image: "",
            twitter_title: "",
            twitter_description: "",
            twitter_image: "",
            canonical_url: "",
            robots: "index,follow",
            structured_data: "",
          }
        );
      } else {
        setMessage(
          "Error loading SEO settings: " + (data.message || "Unknown error")
        );
      }
    } catch (error) {
      setMessage("Error loading SEO settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSeoSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const response = await fetch(
        config.getAdminApiUrl("update_seo_settings.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(seoSettings),
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("SEO settings updated successfully!");
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(
          "Error updating SEO settings: " + (data.message || "Unknown error")
        );
      }
    } catch (error) {
      setMessage("Error updating SEO settings: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSeoSettings({
      page_title: "",
      meta_description: "",
      meta_keywords: "",
      og_title: "",
      og_description: "",
      og_image: "",
      twitter_title: "",
      twitter_description: "",
      twitter_image: "",
      canonical_url: "",
      robots: "index,follow",
      structured_data: "",
    });
    setMessage("Form reset to default values");
    setTimeout(() => setMessage(""), 3000);
  };

  if (loading) {
    return (
      <div className="seo-manager">
        <div className="manager-header">
          <h2>SEO Management</h2>
          <p>Manage search engine optimization settings for your website</p>
        </div>
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading SEO settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="seo-manager">
      <div className="manager-header">
        <h2>SEO Management</h2>
        <p>Manage search engine optimization settings for your website</p>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("Error") ? "alert-danger" : "alert-success"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="seo-form">
        <div className="form-section">
          <h3>Basic SEO Settings</h3>

          <div className="form-group">
            <label htmlFor="page_title">Page Title *</label>
            <input
              type="text"
              id="page_title"
              name="page_title"
              value={seoSettings.page_title}
              onChange={handleInputChange}
              placeholder="Enter page title (recommended: 50-60 characters)"
              maxLength="60"
              required
            />
            <small>
              Recommended length: 50-60 characters. Current:{" "}
              {seoSettings.page_title.length}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="meta_description">Meta Description *</label>
            <textarea
              id="meta_description"
              name="meta_description"
              value={seoSettings.meta_description}
              onChange={handleInputChange}
              placeholder="Enter meta description (recommended: 150-160 characters)"
              maxLength="160"
              rows="3"
              required
            />
            <small>
              Recommended length: 150-160 characters. Current:{" "}
              {seoSettings.meta_description.length}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="meta_keywords">Meta Keywords</label>
            <input
              type="text"
              id="meta_keywords"
              name="meta_keywords"
              value={seoSettings.meta_keywords}
              onChange={handleInputChange}
              placeholder="Enter keywords separated by commas"
            />
            <small>
              Keywords separated by commas (less important for modern SEO but
              still used by some search engines)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="canonical_url">Canonical URL</label>
            <input
              type="url"
              id="canonical_url"
              name="canonical_url"
              value={seoSettings.canonical_url}
              onChange={handleInputChange}
              placeholder="https://example.com/page"
            />
            <small>
              The canonical URL for this page (prevents duplicate content
              issues)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="robots">Robots Meta Tag</label>
            <select
              id="robots"
              name="robots"
              value={seoSettings.robots}
              onChange={handleInputChange}
            >
              <option value="index,follow">Index, Follow</option>
              <option value="noindex,follow">No Index, Follow</option>
              <option value="index,nofollow">Index, No Follow</option>
              <option value="noindex,nofollow">No Index, No Follow</option>
            </select>
            <small>
              Controls how search engines should index and follow links on this
              page
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3>Open Graph (Facebook & Social Media)</h3>

          <div className="form-group">
            <label htmlFor="og_title">OG Title</label>
            <input
              type="text"
              id="og_title"
              name="og_title"
              value={seoSettings.og_title}
              onChange={handleInputChange}
              placeholder="Enter Open Graph title"
              maxLength="60"
            />
            <small>
              Title that appears when shared on Facebook and other social
              platforms
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="og_description">OG Description</label>
            <textarea
              id="og_description"
              name="og_description"
              value={seoSettings.og_description}
              onChange={handleInputChange}
              placeholder="Enter Open Graph description"
              maxLength="200"
              rows="3"
            />
            <small>
              Description that appears when shared on social media platforms
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="og_image">OG Image URL</label>
            <input
              type="url"
              id="og_image"
              name="og_image"
              value={seoSettings.og_image}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
            <small>
              Image URL that appears when shared on social media (recommended:
              1200x630px)
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3>Twitter Card</h3>

          <div className="form-group">
            <label htmlFor="twitter_title">Twitter Title</label>
            <input
              type="text"
              id="twitter_title"
              name="twitter_title"
              value={seoSettings.twitter_title}
              onChange={handleInputChange}
              placeholder="Enter Twitter title"
              maxLength="60"
            />
            <small>Title that appears when shared on Twitter</small>
          </div>

          <div className="form-group">
            <label htmlFor="twitter_description">Twitter Description</label>
            <textarea
              id="twitter_description"
              name="twitter_description"
              value={seoSettings.twitter_description}
              onChange={handleInputChange}
              placeholder="Enter Twitter description"
              maxLength="200"
              rows="3"
            />
            <small>Description that appears when shared on Twitter</small>
          </div>

          <div className="form-group">
            <label htmlFor="twitter_image">Twitter Image URL</label>
            <input
              type="url"
              id="twitter_image"
              name="twitter_image"
              value={seoSettings.twitter_image}
              onChange={handleInputChange}
              placeholder="https://example.com/twitter-image.jpg"
            />
            <small>
              Image URL that appears when shared on Twitter (recommended:
              1200x600px)
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3>Structured Data (JSON-LD)</h3>

          <div className="form-group">
            <label htmlFor="structured_data">Structured Data</label>
            <textarea
              id="structured_data"
              name="structured_data"
              value={seoSettings.structured_data}
              onChange={handleInputChange}
              placeholder='{"@context": "https://schema.org", "@type": "Organization", "name": "Your Company"}'
              rows="8"
            />
            <small>
              JSON-LD structured data for rich snippets (optional but
              recommended for better search results)
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save SEO Settings
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
          >
            <i className="fas fa-undo"></i> Reset to Default
          </button>
        </div>
      </form>

      <div className="seo-preview">
        <h3>SEO Preview</h3>
        <div className="preview-section">
          <h4>Search Engine Result Preview</h4>
          <div className="serp-preview">
            <div className="serp-title">
              {seoSettings.page_title || "Page Title"}
            </div>
            <div className="serp-url">
              {seoSettings.canonical_url || "https://example.com"}
            </div>
            <div className="serp-description">
              {seoSettings.meta_description ||
                "Meta description will appear here..."}
            </div>
          </div>
        </div>

        <div className="preview-section">
          <h4>Social Media Preview</h4>
          <div className="social-preview">
            <div className="social-title">
              {seoSettings.og_title ||
                seoSettings.page_title ||
                "Social Media Title"}
            </div>
            <div className="social-description">
              {seoSettings.og_description ||
                seoSettings.meta_description ||
                "Social media description..."}
            </div>
            {seoSettings.og_image && (
              <div className="social-image">
                <img
                  src={seoSettings.og_image}
                  alt="OG Image"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOManager;
